import Loading from "@app/components/Loading";
import { currentUserRoleAtom } from "@app/store/userStore";
import { Accordion } from "@design-system/DataDisplay/Accordion";
import { DetailChip } from "@design-system/DataDisplay/DetailChip";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { PageContainer } from "@design-system/Layout/PageContainer";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { UserRole } from "@generated/client/graphql";
import { useAtomValue } from "jotai";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams } from "react-router-dom";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import CreateFrameworkProjectModal from "../components/CreateFrameworkProjectModal";
import { GenerateCdpWorkplanModal } from "../components/GenerateWorkplanModal/CdpWorkplanModal";
import { GenerateEvsWorkplanModal } from "../components/GenerateWorkplanModal/EvsWorkplanModal";
import { useFrameworkCategory } from "../data";
import { FrameworkProjectTable } from "./components/Table";
import { useFrameworkTranslations } from "./i18n";
import { useFrameworkCategoryBreadcrumb } from "./useFrameworkCategoryBreadcrumb";
import { useFrameworkGuidance } from "./useFrameworkGuidance";

export default function FrameworkCategory() {
    const { framework_group_id } = useParams();
    const { t, keys } = useFrameworkTranslations();

    const navigate = useNavigate();
    const { frameworkCategory: framework, isPending } =
        useFrameworkCategory(framework_group_id);

    const { displayedGuidance, tooLongGuidance } = useFrameworkGuidance(
        framework?.guide ?? undefined,
    );

    useFrameworkCategoryBreadcrumb();

    const handleDemoLinkClick = () => {
        window.open(framework?.demoLink ?? "", "_blank");
    };

    const handleOfficialWebsiteClick = () => {
        window.open(framework?.officialWebsite ?? "", "_blank");
    };

    const { role } = useAtomValue(currentUserRoleAtom);

    const showGenerateWorkplanModal =
        role === UserRole.BeavrAdmin &&
        (framework_group_id === "cdp-main" ||
            framework_group_id === "evs-questionnaire");

    if (!framework_group_id) {
        navigate("../../");
        return null;
    }

    if (isPending) {
        return (
            <PageContainer p="4">
                <Loading size="large" />
            </PageContainer>
        );
    }

    if (!framework) {
        navigate("../../");
        return;
    }

    return (
        <PageContainer p="4">
            <FlexCol gap="7">
                <FlexRow alignSelf="stretch" alignItems="center">
                    <FlexCol w="full">
                        <Text variant="header1" color="primary">
                            {framework.name}
                        </Text>
                        <Text color="secondary">{framework.description}</Text>
                    </FlexCol>
                </FlexRow>

                <FlexCol gap="4">
                    <FlexRow justifyContent="between" alignItems="center">
                        <Text variant="header3" color="primary">
                            {t(keys.table_section_title)}
                        </Text>
                        <FlexRow gap="2">
                            <CreateFrameworkProjectModal
                                categoryId={framework_group_id}
                                key={framework_group_id}
                            />
                            {showGenerateWorkplanModal && (
                                <Dropdown>
                                    <Dropdown.Trigger>
                                        <Button variant="tonal" size="sm">
                                            <Button.Icon name="more" />
                                        </Button>
                                    </Dropdown.Trigger>
                                    <Dropdown.Content hasPortal>
                                        <Dropdown.Item>
                                            {framework_group_id ===
                                                "cdp-main" && (
                                                <GenerateCdpWorkplanModal
                                                    frameworkId={
                                                        framework_group_id
                                                    }
                                                />
                                            )}
                                            {framework_group_id ===
                                                "evs-questionnaire" && (
                                                <GenerateEvsWorkplanModal />
                                            )}
                                        </Dropdown.Item>
                                    </Dropdown.Content>
                                </Dropdown>
                            )}
                        </FlexRow>
                    </FlexRow>

                    <FrameworkProjectTable
                        frameworkProjects={framework.projects}
                    />
                </FlexCol>

                <FlexCol gap="4">
                    <FlexCol gap="1" alignSelf="stretch">
                        <Text variant="header3" color="primary">
                            {t(keys.about_section_title)}
                        </Text>
                        <FlexRow gap="0.5">
                            <Button
                                variant="textLink"
                                size="sm"
                                disabled={!framework.demoLink}
                                onClick={handleDemoLinkClick}
                            >
                                <Button.Icon name="video" />
                                {t(keys.about_demo_link)}
                            </Button>

                            <Button
                                variant="textLink"
                                size="sm"
                                disabled={!framework.officialWebsite}
                                onClick={handleOfficialWebsiteClick}
                            >
                                <Button.Icon name="external" />
                                {t(keys.about_official_website)}
                            </Button>
                        </FlexRow>
                    </FlexCol>

                    <FlexRow gap="4">
                        <FlexCol p="4" className="border rounded-lg" w="full">
                            <FlexCol gap="4">
                                <Text variant="header3" color="primary">
                                    {t(keys.faq)}
                                </Text>
                                <Accordion type="multiple">
                                    {framework.faq.map((faq) => (
                                        <Accordion.Item
                                            key={faq.question}
                                            value={faq.question}
                                        >
                                            <Accordion.Trigger>
                                                <Text
                                                    variant="body-bold"
                                                    className="text-neutral-700 text-left"
                                                >
                                                    {faq.question}
                                                </Text>
                                            </Accordion.Trigger>
                                            <Accordion.Content>
                                                <Text
                                                    variant="body"
                                                    className="text-neutral-700 text-left"
                                                >
                                                    {faq.answer}
                                                </Text>
                                            </Accordion.Content>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            </FlexCol>
                        </FlexCol>
                        <FlexCol
                            br="lg"
                            p="4"
                            className="border"
                            w="full"
                            h="fit"
                            gap="6"
                        >
                            <FlexRow gap="2">
                                <Text variant="header3" color="primary">
                                    {t(keys.expertise_guidance)}
                                </Text>
                                <DetailChip>
                                    <DetailChip.Icon name="logo" />
                                </DetailChip>
                            </FlexRow>
                            {framework.guide ? (
                                <FlexCol>
                                    <ReactMarkdown
                                        className="markdown"
                                        remarkPlugins={[
                                            remarkGfm,
                                            remarkBreaks,
                                        ]}
                                    >
                                        {displayedGuidance}
                                    </ReactMarkdown>

                                    {tooLongGuidance && (
                                        <Modal>
                                            <Modal.Trigger asChild>
                                                <FlexRow justifyContent="end">
                                                    <Button
                                                        variant="text"
                                                        size="sm"
                                                        className="text-beavrGreen"
                                                    >
                                                        {t(
                                                            keys.view_full_guidance,
                                                        )}
                                                        <Button.Icon name="circleHelp" />
                                                    </Button>
                                                </FlexRow>
                                            </Modal.Trigger>
                                            <Modal.Overlay
                                                scrollable
                                                centerContent
                                            >
                                                <Modal.Content>
                                                    <Modal.Header withClose>
                                                        <Modal.Title>
                                                            {t(
                                                                keys.full_guidance_title,
                                                            )}
                                                        </Modal.Title>
                                                    </Modal.Header>
                                                    <Modal.Body className="p-6 max-h-[600px] overflow-y-auto">
                                                        <FlexCol gap="4">
                                                            <ReactMarkdown
                                                                className="markdown text-primary text-sm"
                                                                remarkPlugins={[
                                                                    remarkGfm,
                                                                    remarkBreaks,
                                                                ]}
                                                            >
                                                                {
                                                                    framework.guide
                                                                }
                                                            </ReactMarkdown>
                                                        </FlexCol>
                                                    </Modal.Body>
                                                </Modal.Content>
                                            </Modal.Overlay>
                                        </Modal>
                                    )}
                                </FlexCol>
                            ) : (
                                <Text variant="body-sm">
                                    {t(keys.expertise_guidance_description)}
                                </Text>
                            )}
                        </FlexCol>
                    </FlexRow>
                </FlexCol>
            </FlexCol>
        </PageContainer>
    );
}
